import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { routes } from '@/routes';
import { getLocaleFromPath } from '@/lib/publicBaseUrl';
import { getNextIntake, cohorts, getCohortStatus } from '@/data/cohorts';
import { products, isZarRegion } from '@/data/pricing';
import { useRegion } from '@/contexts/RegionContext';
import { Price } from '@/components/ui/Price';
import { SocialShare } from '@/components/ui/SocialShare';
import { 
  Award, 
  BookOpen, 
  Users, 
  CheckCircle2, 
  Calendar, 
  Clock, 
  ArrowRight, 
  Layers, 
  ShieldCheck, 
  FileText, 
  Sparkles, 
  GraduationCap, 
  Compass, 
  ChevronDown, 
  Check, 
  Building2 
} from 'lucide-react';

function formatDeadline(isoDate: string): string {
  if (!isoDate) return '';
  const dateStr = isoDate.includes('T') ? isoDate.split('T')[0] : isoDate;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export const PractitionerCertificationPage = () => {
  const { regionCode, locale } = useRegion();
  const location = useLocation();
  const currentLocale = getLocaleFromPath(location.pathname);
  const routeConfig = routes.find(r => r.path === '/practitioner-certification');
  const localeSeo = currentLocale && routeConfig?.seo.byLocale ? (routeConfig.seo.byLocale as any)[currentLocale] : undefined;
  const pageTitle = localeSeo?.title || routeConfig?.seo.title || 'Enneagram Practitioner Certification | FIVE LENS';

  const isZar = isZarRegion(regionCode, locale);
  const nextCohort = getNextIntake(1);
  const furtherCohorts = cohorts.filter(c => c.level === 1 && getCohortStatus(c) === 'Open' && (!nextCohort || c.id !== nextCohort.id));
  const product = products['practitioner-certification'];

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const modules = [
    {
      number: "01",
      title: "Foundations of the FIVE LENS Methodology & Enneagram Dynamics",
      duration: "Self-Paced Prep + Live Intensive",
      description: "Establish deep theoretical grounding in the 9 Enneagram styles, instinctual biases, core motivations, and defensive patterns. Learn how vertical development differentiates developmental levels within each personality style.",
      topics: [
        "The 9 Enneagram core motivations, fixations, and emotional habits",
        "Wings, stress integration, and disintegration growth pathways",
        "Instinctual subtypes (Self-Preservation, One-to-One, Social)",
        "Historical origins, contemporary psychometrics, and statistical validity"
      ]
    },
    {
      number: "02",
      title: "The Five Interdependent Psychological Lenses",
      duration: "Live Interactive Cohort",
      description: "Move beyond single-typology diagnostics. Examine how the five separate lenses interact dynamically to shape executive presence, emotional resilience, and team interactions.",
      topics: [
        "Lens 1 (The Enneagram): Uncovering unconscious drivers and core fears",
        "Lens 2 (Personal Mastery & Maturity): Measuring self-regulation, self-efficacy and vertical leadership maturity",
        "Lens 3 (Emotional Resilience): Assessing capacity to adapt, recover and stay effective under strain",
        "Lens 4 (Social Drives): Analysing status, connection and belonging within teams",
        "Lens 5 (Energy Centres): Balancing the Instinctual, Emotional and Intellectual centres"
      ]
    },
    {
      number: "03",
      title: "Psychometric Assessment & Advanced Report Deconstruction",
      duration: "Cohort Simulation & Analysis",
      description: "Master the full 28-page personal diagnostic report. Learn how to interpret nuanced scores, identify profile incongruencies, and unpack defensive masking in senior executives.",
      topics: [
        "Interpreting standard scores, bell curves, and percentile rankings",
        "Identifying incongruencies between self-perception and psychometric patterns",
        "Diagnosing burnout indicators, emotional exhaustion, and stress triggers",
        "Generating executive summaries and developmental roadmaps"
      ]
    },
    {
      number: "04",
      title: "Transformational Coaching & The 6-Stage Debrief Protocol",
      duration: "Observed Live Practice",
      description: "Acquire our structured, proprietary 90-minute client debrief methodology. Guide clients from psychological safety and defensive awareness through to actionable, transformative commitments.",
      topics: [
        "Establishing rapport and psychological safety for vulnerable inquiry",
        "Precision inquiry: Asking questions that unlock unhelpful behavioral loops",
        "Navigating client resistance, defensiveness, or profile skepticism",
        "Designing 90-day action plans aligned with organizational and personal goals"
      ]
    },
    {
      number: "05",
      title: "Supervised Practicum, Ethical Standards & Platform Mastery",
      duration: "Client Debrief + 1-on-1 Faculty Moderation",
      description: "Apply your training in the field with a live client debrief. Receive one-to-one mentoring feedback from a Master Practitioner, review professional ethics, and activate your practitioner portal.",
      topics: [
        "Conducting an official client assessment and recorded or observed debrief",
        "One-on-one supervision and written evaluation from a FIVE LENS Moderator",
        "Ethical guidelines, confidentiality protocols, and professional boundaries",
        "Practitioner Portal administration: Assessment dispatch, credit management, and white-label reporting"
      ]
    }
  ];

  const faqs = [
    {
      q: "What qualification and credential do I receive upon completion?",
      a: "Upon successful completion of all coursework, cohort intensives, and the moderated client debrief, you will be awarded the official designation of Certified FIVE LENS Enneagram Practitioner. You will receive an authenticated certificate and digital verification badge for your website and LinkedIn profile."
    },
    {
      q: "How does the online blended format work across different time zones?",
      a: "The certification blends approximately 8 to 10 hours of self-paced digital preparation with six 3-hour live interactive sessions held via Zoom over approximately 3 weeks. Cohort timings are scheduled between 14h00 and 17h00 South Africa Standard Time (SAST / UTC+2), which comfortably accommodates participants across the UK, Europe, Africa, the Middle East, and morning sessions for the Americas."
    },
    {
      q: "Are client assessment credits included in the certification fee?",
      a: "Yes. Your certification package includes initial complimentary client assessment credits so you can administer full diagnostics to your practicum clients immediately without additional upfront cost."
    },
    {
      q: "Can I brand the diagnostic reports with my own company or coaching practice logo?",
      a: "Yes. As a certified practitioner, you gain access to our Practitioner Portal which includes co-branding capabilities, enabling you to append your firm's logo and contact details to all client diagnostic PDF reports."
    },
    {
      q: "Does this certification qualify for ICF Continuing Coach Education (CCE) units?",
      a: "Yes. The certification is recognized for ICF Continuing Coach Education (CCE) units across both Core Competencies and Resource Development, supporting your initial credentialing or renewal."
    },
    {
      q: "What ongoing support is available after certification?",
      a: "Graduates enjoy lifetime membership in our Global Practitioner Network, which includes complimentary monthly masterclass webinars, peer supervision circles, continuing development modules, and optional listing in the Global Practitioner Directory."
    }
  ];

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "FIVE LENS Enneagram Practitioner Certification",
    "description": "ICF-accredited certification programme teaching coaches to debrief Five Lens reports with clients. Covers the Enneagram plus four additional lenses of psychological insight for one-on-one coaching and personal development.",
    "provider": {
      "@type": "Organization",
      "name": "FIVE LENS Enneagram",
      "url": "https://www.fivelensenneagram.com"
    },
    "educationalCredentialAwarded": "FIVE LENS Enneagram Practitioner Certification",
    "timeRequired": "P8W",
    "courseMode": ["online", "blended"],
    "inLanguage": "en",
    "offers": [
      {
        "@type": "Offer",
        "price": "12000",
        "priceCurrency": "ZAR",
        "availability": "https://schema.org/InStock",
        "url": "https://www.fivelensenneagram.com/practitioner-certification"
      },
      {
        "@type": "Offer",
        "price": "900",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock",
        "url": "https://www.fivelensenneagram.com/practitioner-certification"
      },
      {
        "@type": "Offer",
        "price": "1200",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://www.fivelensenneagram.com/practitioner-certification"
      }
    ],
    "hasCourseInstance": cohorts.filter(c => c.level === 1 && getCohortStatus(c) === 'Open').map(c => ({
      "@type": "CourseInstance",
      "courseMode": "online",
      "startDate": c.startDate,
      "endDate": c.endDate,
      "courseWorkload": "8-10 hours self-paced online + 6 x 3-hour live Zoom sessions",
      "location": {
        "@type": "VirtualLocation",
        "url": `https://www.fivelensenneagram.com/cohorts/${c.slug}`
      }
    }))
  };

  return (
    <main className="min-h-screen bg-ground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      </Helmet>
      <SocialShare title={pageTitle} />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
        <Breadcrumb items={[{ label: 'For Coaches and Practitioners', href: '/practitioners' }, { label: 'Practitioner Certification' }]} />
      </div>

      {/* Hero Section */}
      <section id="offering-hero" className="pt-12 pb-16 px-6 max-w-7xl mx-auto">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-rule-3/10 text-rule-3 text-xs font-bold uppercase tracking-widest mb-6 border border-rule-3/20">
            <Award className="w-4 h-4 text-rule-3" />
            Accredited Professional Qualification • Level 1
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-ink mb-6 leading-tight font-normal">
            FIVE LENS Enneagram Practitioner Certification
          </h1>
          
          <p className="text-xl text-ink/80 mb-8 max-w-3xl leading-relaxed font-light">
            Equip your practice to diagnose and solve complex personal, leadership, and organizational friction. A rigorous, multidimensional accreditation combining vertical maturity, emotional intelligence, and psychometric precision.
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-10">
            <a 
              href="#curriculum" 
              className="inline-flex items-center gap-2 bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/80 transition-colors"
            >
              Explore Curriculum <ArrowRight className="w-4 h-4" />
            </a>
            <a 
              href="#cohort-pricing" 
              className="inline-flex items-center gap-2 bg-white text-ink border border-ground-deep px-8 py-4 uppercase tracking-widest text-xs font-bold hover:border-rule-3 hover:text-rule-3 transition-colors"
            >
              View Dates & Enrolment
            </a>
            <Link 
              to="/icf-cce-points" 
              className="inline-flex items-center gap-2 text-ink/70 hover:text-rule-3 text-xs font-semibold uppercase tracking-wider px-4 py-4 transition-colors"
            >
              ICF Accreditation Guide &rarr;
            </Link>
          </div>

          {/* Quick Specification Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 bg-white border border-ground-deep shadow-sm">
            <div>
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-ink/50 mb-1">Learning Format</span>
              <span className="text-sm font-bold text-ink flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-rule-3" /> Blended Virtual
              </span>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-ink/50 mb-1">Course Cohort</span>
              <span className="text-sm font-bold text-ink flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-rule-3" /> 6 Live Sessions
              </span>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-ink/50 mb-1">Credential</span>
              <span className="text-sm font-bold text-ink flex items-center gap-1.5">
                <GraduationCap className="w-4 h-4 text-rule-3" /> Certified Practitioner
              </span>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-ink/50 mb-1">Accreditation</span>
              <span className="text-sm font-bold text-ink flex items-center gap-1.5">
                <Award className="w-4 h-4 text-rule-3" /> ICF CCE Approved
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* The FIVE LENS Difference Section */}
      <section id="framework-difference" className="py-20 px-6 bg-white border-t border-b border-ground-deep">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-3">
              The Multidimensional Edge
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-ink mb-6">
              Why FIVE LENS Transcends Conventional Typology Assessments
            </h2>
            <p className="text-lg text-ink/70 leading-relaxed font-light">
              Most personality tools identify what someone does or categorizes them into a rigid box. The FIVE LENS model measures why people behave the way they do across five interdependent dimensions of human psychological functioning.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-ground border border-ground-deep hover:border-rule-3 transition-colors">
              <div className="w-12 h-12 bg-white border border-ground-deep flex items-center justify-center text-rule-3 mb-6">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-serif font-bold text-ink mb-3">5 Interdependent Lenses</h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Seamlessly integrates Enneagram core motivations with Personal Mastery, Emotional Resilience, Social Drives, and the three Energy Centres (Instinctual, Emotional and Intellectual).
              </p>
            </div>

            <div className="p-8 bg-ground border border-ground-deep hover:border-rule-3 transition-colors">
              <div className="w-12 h-12 bg-white border border-ground-deep flex items-center justify-center text-rule-3 mb-6">
                <Compass className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-serif font-bold text-ink mb-3">Vertical Developmental Maturity</h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Rather than treating type as static, our psychometric assesses how effectively an individual navigates complexity, self-corrects under stress, and evolves their leadership capacity.
              </p>
            </div>

            <div className="p-8 bg-ground border border-ground-deep hover:border-rule-3 transition-colors">
              <div className="w-12 h-12 bg-white border border-ground-deep flex items-center justify-center text-rule-3 mb-6">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-serif font-bold text-ink mb-3">Enterprise-Grade Reliability</h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Backed by 30+ years of statistical validation in corporate environments, providing coaches and HR leaders with data that executive sponsors immediately respect and trust.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive 5-Module Curriculum */}
      <section id="curriculum" className="py-20 px-6 max-w-7xl mx-auto">
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-3">
            Syllabus & Learning Journey
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-ink mb-6">
            A Structured, Rigorous Curriculum for Transformational Practice
          </h2>
          <p className="text-lg text-ink/70 leading-relaxed font-light">
            Designed for coaches, organizational consultants, and people-development leaders who need both academic depth and practical, revenue-generating tools.
          </p>
        </div>

        <div className="space-y-6">
          {modules.map((m) => (
            <div 
              key={m.number} 
              id={`module-${m.number}`}
              className="bg-white border border-ground-deep p-8 lg:p-10 shadow-sm hover:border-rule-3/60 transition-all"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 pb-6 border-b border-ground-deep">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 bg-ink text-white">
                      Module {m.number}
                    </span>
                    <span className="text-xs uppercase tracking-wider text-rule-3 font-semibold">
                      {m.duration}
                    </span>
                  </div>
                  <h3 className="text-2xl font-serif text-ink font-normal">{m.title}</h3>
                </div>
              </div>

              <p className="mt-6 text-ink/80 text-base leading-relaxed font-light mb-6">
                {m.description}
              </p>

              <div>
                <h4 className="text-xs uppercase tracking-wider font-bold text-ink mb-4">Core Knowledge & Competencies Acquired:</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {m.topics.map((t, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-ink/70">
                      <CheckCircle2 className="w-4 h-4 text-rule-3 mt-0.5 shrink-0" />
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Learning Phases & Delivery Architecture */}
      <section id="delivery-format" className="py-20 px-6 bg-white border-t border-b border-ground-deep">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-3">
              Delivery Architecture
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-ink mb-6">
              How the Certification Journey Unfolds
            </h2>
            <p className="text-lg text-ink/70 font-light">
              A balanced blend of independent study, interactive live faculty sessions, and real-world supervised coaching.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-ground border border-ground-deep p-6 relative">
              <span className="w-8 h-8 bg-ink text-white flex items-center justify-center font-bold text-sm mb-4">01</span>
              <h4 className="font-bold text-ink mb-2">Personal Assessment</h4>
              <p className="text-sm text-ink/70 leading-relaxed mb-4">
                Complete your own full 28-page FIVE LENS assessment and receive a private 90-minute 1-on-1 debrief with a Master Moderator.
              </p>
              <span className="text-xs font-semibold text-rule-3 uppercase tracking-wider">Week 1 • Discovery</span>
            </div>

            <div className="bg-ground border border-ground-deep p-6 relative">
              <span className="w-8 h-8 bg-ink text-white flex items-center justify-center font-bold text-sm mb-4">02</span>
              <h4 className="font-bold text-ink mb-2">Self-Paced Prep</h4>
              <p className="text-sm text-ink/70 leading-relaxed mb-4">
                Review 8 to 10 hours of rich online modules covering the psychometric constructs, scoring metrics, and debrief frameworks.
              </p>
              <span className="text-xs font-semibold text-rule-3 uppercase tracking-wider">Pre-Course • 8-10 Hours</span>
            </div>

            <div className="bg-ground border border-ground-deep p-6 relative">
              <span className="w-8 h-8 bg-rule-3 text-white flex items-center justify-center font-bold text-sm mb-4">03</span>
              <h4 className="font-bold text-ink mb-2">Live Cohort Intensives</h4>
              <p className="text-sm text-ink/70 leading-relaxed mb-4">
                Six 3-hour live Zoom sessions over ~3 weeks. Participate in observed simulations, case analysis, and group debrief exercises.
              </p>
              <span className="text-xs font-semibold text-rule-3 uppercase tracking-wider">Weeks 2-4 • 18 Hours Live</span>
            </div>

            <div className="bg-ground border border-ground-deep p-6 relative">
              <span className="w-8 h-8 bg-ink text-white flex items-center justify-center font-bold text-sm mb-4">04</span>
              <h4 className="font-bold text-ink mb-2">Supervised Practicum</h4>
              <p className="text-sm text-ink/70 leading-relaxed mb-4">
                Conduct a real client debrief, submit your case assignment, and receive detailed written and verbal supervision from faculty.
              </p>
              <span className="text-xs font-semibold text-rule-3 uppercase tracking-wider">Culmination • Accreditation</span>
            </div>
          </div>
        </div>
      </section>

      {/* What Is Included in the Certification */}
      <section id="included-features" className="py-20 px-6 max-w-7xl mx-auto">
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-3">
            Comprehensive Toolkit
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-ink mb-6">
            Everything Included in Your Practitioner Qualification
          </h2>
          <p className="text-lg text-ink/70 leading-relaxed font-light">
            You receive not just academic instruction, but a turnkey commercial platform to integrate into your existing coaching, consulting, or internal HR practice.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <Award className="w-8 h-8 text-rule-3 mb-6" />
            <h3 className="text-xl font-serif font-bold text-ink mb-3">Accredited Credential</h3>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">
              Official certificate and digital badge as an Accredited FIVE LENS Practitioner, eligible for ICF Continuing Coach Education (CCE) units.
            </p>
            <ul className="space-y-2 text-xs text-ink/80">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Verifiable digital credential</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> ICF CCE credits approved</li>
            </ul>
          </div>

          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <Layers className="w-8 h-8 text-rule-3 mb-6" />
            <h3 className="text-xl font-serif font-bold text-ink mb-3">Practitioner Portal & Credits</h3>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">
              Full administrative dashboard access to send assessment links, monitor completions, and generate 28-page PDF diagnostic reports.
            </p>
            <ul className="space-y-2 text-xs text-ink/80">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Starter assessment credits included</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Co-branding with your practice logo</li>
            </ul>
          </div>

          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <BookOpen className="w-8 h-8 text-rule-3 mb-6" />
            <h3 className="text-xl font-serif font-bold text-ink mb-3">Facilitator Toolkit & Guides</h3>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">
              Turnkey coaching resources including 90-minute debrief question guides, executive reflection journals, and presentation slide decks.
            </p>
            <ul className="space-y-2 text-xs text-ink/80">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Debriefing manuals & question banks</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Client development templates</li>
            </ul>
          </div>

          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <Users className="w-8 h-8 text-rule-3 mb-6" />
            <h3 className="text-xl font-serif font-bold text-ink mb-3">Lifetime Peer Community</h3>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">
              Join over 700 certified practitioners worldwide. Access monthly masterclasses, peer supervision groups, and global knowledge exchanges.
            </p>
            <ul className="space-y-2 text-xs text-ink/80">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Monthly masterclasses</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Peer supervision circles</li>
            </ul>
          </div>

          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <Building2 className="w-8 h-8 text-rule-3 mb-6" />
            <h3 className="text-xl font-serif font-bold text-ink mb-3">Global Directory Listing</h3>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">
              Showcase your credentials on the official public FIVE LENS Practitioner Directory to receive corporate coaching and debrief referrals.
            </p>
            <ul className="space-y-2 text-xs text-ink/80">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Searchable practitioner profile</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Direct corporate inquiry linkage</li>
            </ul>
          </div>

          <div className="bg-white p-8 border border-ground-deep shadow-sm">
            <Sparkles className="w-8 h-8 text-rule-3 mb-6" />
            <h3 className="text-xl font-serif font-bold text-ink mb-3">Level 2 Progression Pathway</h3>
            <p className="text-sm text-ink/70 leading-relaxed mb-4">
              Qualify for the advanced Level 2 Team Facilitator Certification, enabling you to deliver team dynamics mapping and corporate trust workshops.
            </p>
            <ul className="space-y-2 text-xs text-ink/80">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Team Facilitator eligibility</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-rule-3" /> Bundled certification options</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section id="target-audience" className="py-20 px-6 bg-ground-alt border-t border-b border-ground-deep">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-3">
                Candidate Profile
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-ink mb-6">
                Who Should Become a Certified Practitioner?
              </h2>
              <p className="text-lg text-ink/70 leading-relaxed font-light mb-8">
                The qualification suits anyone who wants a rigorous, multidimensional diagnostic to support executive leaders and high-performing teams.
              </p>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-white border border-ground-deep flex items-center justify-center shrink-0 text-rule-3">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink text-base">Executive & Leadership Coaches</h4>
                    <p className="text-sm text-ink/70">Equip your executive engagements with actionable psychometrics that surface unexamined limiting beliefs and accelerate vertical leadership maturity.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-white border border-ground-deep flex items-center justify-center shrink-0 text-rule-3">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink text-base">Organizational Development & HR Directors</h4>
                    <p className="text-sm text-ink/70">Establish a coherent, unified framework across your enterprise for talent development, high-potential programs, and culture change initiatives.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-white border border-ground-deep flex items-center justify-center shrink-0 text-rule-3">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink text-base">Independent Consultants & Facilitators</h4>
                    <p className="text-sm text-ink/70">Differentiate your commercial offering with a proprietary diagnostic that clients cannot get from entry-level personality providers.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 md:p-12 border border-ground-deep shadow-sm">
              <h3 className="text-2xl font-serif text-ink mb-4">Entry Requirements</h3>
              <p className="text-sm text-ink/70 leading-relaxed mb-6">
                Level 1 is the entry point to FIVE LENS certification. No prior qualification or coaching training is required.
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-rule-3 mt-1 shrink-0" />
                  <span className="text-sm text-ink/80">Open to coaches, HR and L&D professionals, consultants, leaders and anyone who develops people.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-rule-3 mt-1 shrink-0" />
                  <span className="text-sm text-ink/80">Complete the 8 to 10 hours of self-paced preparation before the live sessions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-rule-3 mt-1 shrink-0" />
                  <span className="text-sm text-ink/80">Attend the six live Zoom sessions.</span>
                </li>
              </ul>

              <div className="p-4 bg-ground border border-ground-deep text-xs text-ink/70">
                <span className="font-bold text-ink">Enrolment note:</span> submitting your registration reserves your place. We confirm your intake date within five working days.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confirmed Intake Dates, Pricing & Registration Card */}
      <section id="cohort-pricing" className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-3">
            Intake Schedule & Investment
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-ink mb-6">
            Secure Your Place in the Next Certified Cohort
          </h2>
          <p className="text-lg text-ink/70 font-light">
            Cohorts are strictly limited to 15 participants to guarantee personalized faculty attention, live supervised feedback, and rich peer exchange.
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white border border-ground-deep shadow-sm p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-3 py-1 bg-rule-3/10 text-rule-3 text-xs font-bold uppercase tracking-widest mb-3 border border-rule-3/20">
                {nextCohort ? 'Level 1 Confirmed Intake' : 'Level 1 Intake'}
              </div>
              <h3 className="text-3xl font-serif text-ink mb-3">
                {nextCohort ? nextCohort.displayDate : 'Next intake: scheduled on request'}
              </h3>
              <p className="text-sm text-ink/70 mb-6">
                {nextCohort
                  ? nextCohort.seatsAvailable != null && nextCohort.seatsTotal != null
                    ? `${nextCohort.seatsAvailable} of ${nextCohort.seatsTotal} seats remaining.`
                    : ''
                  : 'Intakes are scheduled on request and confirmed once a cohort is formed.'}
              </p>

              <div className="space-y-4 text-sm text-ink/80 border-t border-b border-ground-deep py-6 mb-6">
                <div>
                  <span className="font-bold text-ink block text-xs uppercase tracking-wider mb-1">
                    Session Schedule
                  </span>
                  <p className="text-ink">
                    {nextCohort?.sessionSchedule || nextCohort?.schedule || '6 sessions, Tuesdays and Thursdays over approximately 3 weeks'}
                  </p>
                </div>
                <div>
                  <span className="font-bold text-ink block text-xs uppercase tracking-wider mb-1">
                    Format & Delivery
                  </span>
                  <p className="text-ink">
                    Blended virtual: 8-10h preparatory online learning + 6 live interactive Zoom sessions + 1-on-1 supervised debrief.
                  </p>
                </div>
              </div>

              {nextCohort && furtherCohorts.length > 0 && (
                <div>
                  <span className="text-xs uppercase tracking-wider font-semibold text-ink/60 block mb-2">
                    Subsequent Confirmed Cohorts:
                  </span>
                  <ul className="space-y-1.5 text-xs text-ink/70">
                    {furtherCohorts.map((c) => (
                      <li key={c.id} className="flex justify-between items-center">
                        <span className="font-medium text-ink">{c.displayDate}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex flex-col justify-center bg-ground p-8 border border-ground-deep">
              <span className="text-xs uppercase tracking-widest font-bold text-rule-3 mb-2">
                Course Investment
              </span>
              <div className="mb-6">
                <Price productKey="practitioner-certification" />
              </div>

              <p className="text-xs text-ink/60 leading-relaxed mb-6">
                Includes all preparatory e-learning, live cohort masterclasses, 1-on-1 personal debrief, supervised practicum assessment, starter client credits, full platform activation, and lifetime community access.
              </p>

              {!nextCohort && (
                <div className="mb-6 p-4 bg-white border border-ground-deep text-xs text-ink/90 leading-relaxed">
                  Your intake date will be confirmed within five working days of enrolment. If the confirmed date does not suit you, you may cancel for a full refund.
                </div>
              )}

              <Link 
                to="/register?product=practitioner-certification" 
                className="block text-center bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/80 transition-colors mb-4"
              >
                Enrol in Next Cohort
              </Link>

              <Link 
                to="/contact" 
                className="block text-center bg-white text-ink border border-ground-deep px-8 py-3 uppercase tracking-widest text-xs font-bold hover:border-rule-3 hover:text-rule-3 transition-colors"
              >
                Speak with an Advisor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section id="certification-faq" className="py-20 px-6 bg-white border-t border-ground-deep">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-3">
              Common Inquiries
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-ink mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-ink/70 font-light">
              Clear answers regarding cohort dynamics, technology requirements, client credits, and credentials.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div 
                  key={index}
                  id={`faq-item-${index}`}
                  className="border border-ground-deep bg-ground/40"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-6 text-left flex items-center justify-between gap-4 font-serif text-lg text-ink font-medium hover:text-rule-3 transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-rule-3 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 pt-2 text-sm text-ink/70 leading-relaxed border-t border-ground-deep/60">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center p-8 bg-ground border border-ground-deep">
            <h3 className="text-xl font-serif text-ink mb-2">Have questions about the certification?</h3>
            <p className="text-sm text-ink/70 mb-6 max-w-xl mx-auto">
              Our team can answer any questions about cohort schedules, the curriculum, or whether Level 1 Practitioner or the Practitioner + Team Facilitator Bundle is the right path for you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center bg-ink text-white px-6 py-3 uppercase tracking-widest text-xs font-bold hover:bg-ink/80 transition-colors"
              >
                Contact Our Team
              </Link>
              <Link 
                to="/cohorts" 
                className="inline-flex items-center justify-center bg-white text-ink border border-ground-deep px-6 py-3 uppercase tracking-widest text-xs font-bold hover:border-rule-3 hover:text-rule-3 transition-colors"
              >
                View All Cohort Dates
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

